import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Copy,
  Search,
  Plus,
  FileText,
  Users,
  Edit3,
  Trash2,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { esignTemplatesApi } from "../../services/api";
import CreateEsignTemplateModal from "./CreateEsignTemplateModal";

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const CATEGORIES = [
  { value: "", label: "All Categories" },
  { value: "retainer", label: "Retainer" },
  { value: "engagement_letter", label: "Engagement Letter" },
  { value: "nda", label: "NDA" },
  { value: "closing", label: "Closing" },
  { value: "consent", label: "Consent" },
  { value: "authorization", label: "Authorization" },
  { value: "general", label: "General" },
];

const CATEGORY_STYLES: Record<string, string> = {
  retainer: "bg-blue-100 text-blue-700",
  engagement_letter: "bg-emerald-100 text-emerald-700",
  nda: "bg-amber-100 text-amber-700",
  closing: "bg-purple-100 text-purple-700",
  consent: "bg-cyan-100 text-cyan-700",
  authorization: "bg-rose-100 text-rose-700",
  general: "bg-slate-100 text-slate-600",
};

function formatCategoryLabel(category: string): string {
  const found = CATEGORIES.find((c) => c.value === category);
  if (found) return found.label;
  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const EsignTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchTemplates();
  }, [categoryFilter, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await esignTemplatesApi.list({
        category: categoryFilter || undefined,
        search: searchTerm || undefined,
      });
      setTemplates(res.data.data || res.data || []);
    } catch (err) {
      console.error("Failed to fetch e-sign templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await esignTemplatesApi.delete(id);
      setTemplates((prev) => prev.filter((t) => t._id !== id));
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("Failed to delete template:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (template: any) => {
    setEditTemplate(template);
    setShowCreateModal(true);
  };

  const handleCreateClose = () => {
    setShowCreateModal(false);
    setEditTemplate(null);
  };

  const handleCreated = () => {
    handleCreateClose();
    fetchTemplates();
  };

  const selectedCategoryLabel =
    CATEGORIES.find((c) => c.value === categoryFilter)?.label || "All Categories";

  return (
    <div>
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1 w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        {/* Category Filter */}
        <div className="relative" ref={categoryRef}>
          <button
            onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors whitespace-nowrap"
          >
            {selectedCategoryLabel}
            <ChevronDown
              className={`h-4 w-4 text-slate-400 transition-transform ${
                categoryDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {categoryDropdownOpen && (
            <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    setCategoryFilter(cat.value);
                    setCategoryDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    categoryFilter === cat.value
                      ? "bg-emerald-50 text-emerald-700 font-medium"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Create Button */}
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors whitespace-nowrap"
          style={{ backgroundColor: PRIMARY }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = HOVER;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = PRIMARY;
          }}
        >
          <Plus className="h-4 w-4" />
          Create Template
        </button>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-slate-300 animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-20">
          <div className="p-4 bg-slate-100 rounded-2xl mb-4">
            <Copy className="h-16 w-16 text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            No templates yet
          </h3>
          <p className="text-sm text-slate-500 mb-6 text-center max-w-sm">
            Create reusable templates to speed up your signing workflow
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = PRIMARY;
            }}
          >
            <Plus className="h-4 w-4" />
            Create Template
          </button>
        </div>
      ) : (
        /* Template Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template, i) => (
            <motion.div
              key={template._id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition-all cursor-pointer group"
            >
              {/* Icon + Name */}
              <div className="flex items-start gap-3 mb-2">
                <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                  <Copy className="h-4 w-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-slate-800 truncate">
                    {template.name}
                  </h4>
                </div>
              </div>

              {/* Description */}
              {template.description && (
                <p className="text-xs text-slate-500 line-clamp-2 mb-3">
                  {template.description}
                </p>
              )}

              {/* Category Badge */}
              <div className="mb-3">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    CATEGORY_STYLES[template.category] || CATEGORY_STYLES.general
                  }`}
                >
                  {formatCategoryLabel(template.category)}
                </span>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                <span className="flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />
                  {template.documents?.length || 0} documents
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {template.defaultSigners?.length || 0} signers
                </span>
              </div>
              {typeof template.usageCount === "number" && (
                <p className="text-xs text-slate-400 mb-4">
                  Used {template.usageCount} {template.usageCount === 1 ? "time" : "times"}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(template);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit
                </button>
                {confirmDeleteId === template._id ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-red-600">Delete?</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                      className="px-2 py-1 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                    >
                      No
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(template._id); }}
                      disabled={deletingId === template._id}
                      className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {deletingId === template._id ? "..." : "Yes"}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(template._id);
                    }}
                    disabled={deletingId === template._id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    {deletingId === template._id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Delete
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Template Modal */}
      <CreateEsignTemplateModal
        open={showCreateModal}
        onClose={handleCreateClose}
        onCreated={handleCreated}
        editTemplate={editTemplate}
      />
    </div>
  );
};

export default EsignTemplatesTab;
